import { memo } from "react";
import { useNavigate } from "react-router-dom";
import { useMessage } from "@vrf/fr-vrf-core-react";
import {
  FrVrfPanel,
  FrVrfTextDisplayField,
  FrVrfFileUploadField,
  FrVrfButton,
} from "@vrf/fr-vrf-web-components-react";
import { ApplicationMessageScreen } from "../../messages/screen-message";
import {
  InDefCommonFlagType,
  RoleType,
  TcktStsTypePrjPlan,
  TcktStsTypePrjPrgrs,
  TcktType,
} from "../../util/general-code/enums/general-codes";
import { EvntGrp } from "../../util/general-code/enums/evnt-grp";
import {
  CrtPjtTcktReq,
  DialogType,
  Result,
  UpdStsReq,
} from "../../pages/project-plan";
import { ENV } from "@vrf/fr-vrf-core";

type Props = {
  pjtId: string;
  project: Result;
  showAndAddProjectStakeholderList: (roleCd: string, teamCd: string) => void;
  showProjectStakeholderList: () => void;
  showProjectInformationDialog: () => void;
  showCostEstimationDialog: () => void;
  showComponentRequirementDialog: () => void;
  showComponentProjectDialog: () => void;
  uploadIfReqSheetRequest: (event: CustomEvent) => void;
  createProjectTicketRequest: (request: CrtPjtTcktReq) => void;
  updateStatusRequest: (request: UpdStsReq) => void;
  loginUserRoleList: string[];
};

enum ActionType {
  SubTask = "1",
  ChangeStatus = "2",
  StatusBack = "3",
}

enum ActionDesctription {
  a0001 = "Enter basic information about this project.",
  a0002 = "Assign a project owner who will be interviewed about IF requirement.",
  a0090 = "Press the action button to register the project.",
  a0101 = "Assign PIC of IF requirement definition.",
  a0190 = "Press the action button to confirm requirement after requesting the PIC of IF Requirement Definition.",
  a0201 = "Enter necessary information for the estimation.",
  a0202 = "Upload the draft IF requirement sheet.",
  a0290 = "Press the action button to confirm the estimation result after requesting DIP Manager.",
  a0301 = "Input estimation result of this project.",
  a0390 = "Check the estimate result and reply the required budget to the Project Owner.",
  a0490 = "Press the action button after sharing the budget information(*) to IE team. * Basically it's PJCD. Department that secures the budget is not GHQ : PO1",
  a0590 = "Press the action button to fix project plan.",
  a0601 = "Enter necessary information to IF requirement sheet.",
  a0602 = "Link related component projects.",
  a0690 = "Press the action button to fix IF requirement sheet.",
  a0701 = `Assign stakeholders. "Project Approver" and "PIC of Component App" for all related components are mandatory.`,
  a0790 = "Press the action botton to start this project.",
  a0801 = "Check the progress of this project on Project Detail page and design / develop interface definition on IF List page.",
  a0890 = "Press the action button to close this project.",
  a9090 = "(If the project plan needs to be modified) Press action button to cancel approval request of complete project plan.",
  a9190 = `(If the estimation needs to be modified) Press the action button to change the status to "Estimation in Progress".`,
  a9290 = `(If IF requirement needs to be modified) Press the action button to change the status to "Under Change IF Requirements".`,
  a9390 = "Press action button to fix IF requirement.",
}

const TEAM_CODE_PROJECT = "PROJECT";
const VRF_HORIZONTAL_SEPARATOR_COLOR = "#e6e6e6";

export const ProjectPlanActionList = memo((props: Props) => {
  const {
    pjtId,
    project,
    showAndAddProjectStakeholderList,
    showProjectStakeholderList,
    showProjectInformationDialog,
    showCostEstimationDialog,
    showComponentRequirementDialog,
    showComponentProjectDialog,
    uploadIfReqSheetRequest,
    createProjectTicketRequest,
    updateStatusRequest,
    loginUserRoleList,
  } = props;

  const dfnSetVerNum = ENV("REACT_APP_DFN_SET_VER_NUM");
  const navigate = useNavigate();
  const { messageForScreen } = useMessage();

  const getActionItems = () => {
    const initialActionItems: ActionItem[] = [
      {
        actionId: "a0001",
        name: "Enter Basic Information",
        description: ActionDesctription.a0001,
        required: true,
        type: ActionType.SubTask,
        actionMethod: () => {
          showProjectInformationDialog();
        },
        prerequisiteActionIds: [],
        enableStatus: [
          {
            tcktTypeCd: TcktType.PROJECT_PLAN,
            tcktStsTypeCd: "00",
          },
        ],
        role: [],
      },
      {
        actionId: "a0002",
        name: "Assign Project Owner",
        description: ActionDesctription.a0002,
        required: true,
        type: ActionType.SubTask,
        actionMethod: () => {
          showAndAddProjectStakeholderList(
            RoleType.PROJECT_OWNER,
            TEAM_CODE_PROJECT,
          );
        },
        prerequisiteActionIds: ["a0001"],
        enableStatus: [
          {
            tcktTypeCd: TcktType.PROJECT_PLAN,
            tcktStsTypeCd: "00",
          },
        ],
        role: [],
      },
      {
        actionId: "a0090",
        name: "Register Project",
        description: ActionDesctription.a0090,
        required: true,
        type: ActionType.ChangeStatus,
        actionMethod: () => {
          createProjectTicketRequest({
            confirmMessage: messageForScreen(
              ApplicationMessageScreen.APP_SCN_00081,
              "Raise",
            ),
            changedMassage: messageForScreen(
              ApplicationMessageScreen.APP_SCN_00090,
            ),
          });
        },
        prerequisiteActionIds: ["a0001", "a0002"],
        enableStatus: [
          {
            tcktTypeCd: TcktType.PROJECT_PLAN,
            tcktStsTypeCd: "00",
          },
        ],
        role: [],
      },
      {
        actionId: "a0101",
        name: "Assign PIC of IF Requirement Definition",
        description: ActionDesctription.a0101,
        required: true,
        type: ActionType.SubTask,
        actionMethod: () => {
          showAndAddProjectStakeholderList(
            RoleType.PIC_OF_IF_RD,
            TEAM_CODE_PROJECT,
          );
        },
        prerequisiteActionIds: [],
        enableStatus: [
          {
            tcktTypeCd: TcktType.PROJECT_PLAN,
            tcktStsTypeCd:
              TcktStsTypePrjPlan.WAITING_FOR_ASSIGNMENT_REQUIREMENTS_DEFINER,
          },
        ],
        role: [RoleType.DIP_MANAGER],
      },
      {
        actionId: "a0190",
        name: "Request Estimation",
        description: ActionDesctription.a0190,
        required: true,
        type: ActionType.ChangeStatus,
        actionMethod: () => {
          updateStatusRequest({
            tcktId: project.planStatus?.tcktId,
            dialogType: DialogType.Confirm,
            confirmMessage: messageForScreen(
              ApplicationMessageScreen.APP_SCN_00082,
            ),
            ok: {
              evntGrpTypeCd: EvntGrp.EVNT_GRP_PJ_010_002,
              changedMassage: messageForScreen(
                ApplicationMessageScreen.APP_SCN_00091,
              ),
            },
          });
        },
        prerequisiteActionIds: ["a0101"],
        enableStatus: [
          {
            tcktTypeCd: TcktType.PROJECT_PLAN,
            tcktStsTypeCd:
              TcktStsTypePrjPlan.WAITING_FOR_ASSIGNMENT_REQUIREMENTS_DEFINER,
          },
        ],
        role: [RoleType.DIP_MANAGER],
      },
      {
        actionId: "a0201",
        name: "Enter requirement information",
        description: ActionDesctription.a0201,
        required: true,
        type: ActionType.SubTask,
        actionMethod: () => {
          showComponentRequirementDialog();
        },
        prerequisiteActionIds: [],
        enableStatus: [
          {
            tcktTypeCd: TcktType.PROJECT_PLAN,
            tcktStsTypeCd: TcktStsTypePrjPlan.ESTIMATION_IN_PROGRESS,
          },
        ],
        role: [RoleType.PIC_OF_IF_RD],
      },
      {
        actionId: "a0202",
        name: "Upload IF Requirement Sheet",
        description: ActionDesctription.a0202,
        required: true,
        type: ActionType.SubTask,
        actionMethod: () => {},
        prerequisiteActionIds: [],
        enableStatus: [
          {
            tcktTypeCd: TcktType.PROJECT_PLAN,
            tcktStsTypeCd: TcktStsTypePrjPlan.ESTIMATION_IN_PROGRESS,
          },
        ],
        role: [RoleType.PIC_OF_IF_RD],
      },
      {
        actionId: "a0290",
        name: "Finish Estimation",
        description: ActionDesctription.a0290,
        required: true,
        type: ActionType.ChangeStatus,
        actionMethod: () => {
          updateStatusRequest({
            tcktId: project.planStatus?.tcktId,
            dialogType: DialogType.Confirm,
            confirmMessage: messageForScreen(
              ApplicationMessageScreen.APP_SCN_00083,
            ),
            ok: {
              evntGrpTypeCd: EvntGrp.EVNT_GRP_PJ_010_003,
              changedMassage: messageForScreen(
                ApplicationMessageScreen.APP_SCN_00092,
              ),
            },
          });
        },
        prerequisiteActionIds: ["a0201", "a0202"],
        enableStatus: [
          {
            tcktTypeCd: TcktType.PROJECT_PLAN,
            tcktStsTypeCd: TcktStsTypePrjPlan.ESTIMATION_IN_PROGRESS,
          },
        ],
        role: [RoleType.PIC_OF_IF_RD],
      },
      {
        actionId: "a0301",
        name: "Enter Estimation Information",
        description: ActionDesctription.a0301,
        required: true,
        type: ActionType.SubTask,
        actionMethod: () => {
          showCostEstimationDialog();
        },
        prerequisiteActionIds: [],
        enableStatus: [
          {
            tcktTypeCd: TcktType.PROJECT_PLAN,
            tcktStsTypeCd: TcktStsTypePrjPlan.UNDER_ESTIMATE_CONFIRMATION,
          },
        ],
        role: [RoleType.DIP_MANAGER],
      },
      {
        actionId: "a0311",
        name: "Re-Estimate",
        description: ActionDesctription.a9190,
        required: false,
        type: ActionType.StatusBack,
        actionMethod: () => {
          updateStatusRequest({
            tcktId: project.planStatus?.tcktId,
            dialogType: DialogType.Confirm,
            confirmMessage: messageForScreen(
              ApplicationMessageScreen.APP_SCN_00089,
            ),
            ok: {
              evntGrpTypeCd: EvntGrp.EVNT_GRP_PJ_010_006,
              changedMassage: messageForScreen(
                ApplicationMessageScreen.APP_SCN_00102,
              ),
            },
          });
        },
        prerequisiteActionIds: [],
        enableStatus: [
          {
            tcktTypeCd: TcktType.PROJECT_PLAN,
            tcktStsTypeCd: TcktStsTypePrjPlan.UNDER_ESTIMATE_CONFIRMATION,
          },
        ],
        role: [RoleType.PIC_OF_IF_RD],
      },
      {
        actionId: "a0390",
        name: "Judgement to Answer Estimate",
        description: ActionDesctription.a0390,
        required: true,
        type: ActionType.ChangeStatus,
        actionMethod: () => {
          updateStatusRequest({
            tcktId: project.planStatus?.tcktId,
            dialogType: DialogType.Judgement,
            confirmMessage: messageForScreen(
              ApplicationMessageScreen.APP_SCN_00084,
            ),
            ok: {
              evntGrpTypeCd:
                project.costEstimation.pjtBdgtAcqstnReqTypeCd ==
                InDefCommonFlagType.YES
                  ? EvntGrp.EVNT_GRP_PJ_010_004
                  : EvntGrp.EVNT_GRP_PJ_010_014,
              changedMassage: messageForScreen(
                ApplicationMessageScreen.APP_SCN_00093,
              ),
            },
            ng: {
              evntGrpTypeCd: EvntGrp.EVNT_GRP_PJ_010_005,
              changedMassage: messageForScreen(
                ApplicationMessageScreen.APP_SCN_00094,
              ),
            },
          });
        },
        prerequisiteActionIds: ["a0301"],
        enableStatus: [
          {
            tcktTypeCd: TcktType.PROJECT_PLAN,
            tcktStsTypeCd: TcktStsTypePrjPlan.UNDER_ESTIMATE_CONFIRMATION,
          },
        ],
        role: [RoleType.DIP_MANAGER],
      },
      {
        actionId: "a0411",
        name: "Re-Estimate",
        description: ActionDesctription.a9190,
        required: false,
        type: ActionType.StatusBack,
        actionMethod: () => {
          updateStatusRequest({
            tcktId: project.planStatus?.tcktId,
            dialogType: DialogType.Confirm,
            confirmMessage: messageForScreen(
              ApplicationMessageScreen.APP_SCN_00089,
            ),
            ok: {
              evntGrpTypeCd: EvntGrp.EVNT_GRP_PJ_010_012,
              changedMassage: messageForScreen(
                ApplicationMessageScreen.APP_SCN_00102,
              ),
            },
          });
        },
        prerequisiteActionIds: [],
        enableStatus: [
          {
            tcktTypeCd: TcktType.PROJECT_PLAN,
            tcktStsTypeCd: TcktStsTypePrjPlan.UNDER_OBTAIN_BUDGET,
          },
        ],
        role: [RoleType.PIC_OF_IF_RD],
      },
      {
        actionId: "a0490",
        name: "Request to Complete Project Plan",
        description: ActionDesctription.a0490,
        required: true,
        type: ActionType.ChangeStatus,
        actionMethod: () => {
          updateStatusRequest({
            tcktId: project.planStatus?.tcktId,
            dialogType: DialogType.Confirm,
            confirmMessage: messageForScreen(
              ApplicationMessageScreen.APP_SCN_00085,
            ),
            ok: {
              evntGrpTypeCd: EvntGrp.EVNT_GRP_PJ_010_007,
              changedMassage: messageForScreen(
                ApplicationMessageScreen.APP_SCN_00095,
              ),
            },
          });
        },
        prerequisiteActionIds: [],
        enableStatus: [
          {
            tcktTypeCd: TcktType.PROJECT_PLAN,
            tcktStsTypeCd: TcktStsTypePrjPlan.UNDER_OBTAIN_BUDGET,
          },
        ],
        role: [RoleType.PROJECT_OWNER],
      },
      {
        actionId: "a0511",
        name: "Re-Estimate",
        description: ActionDesctription.a9190,
        required: false,
        type: ActionType.StatusBack,
        actionMethod: () => {
          updateStatusRequest({
            tcktId: project.planStatus?.tcktId,
            dialogType: DialogType.Confirm,
            confirmMessage: messageForScreen(
              ApplicationMessageScreen.APP_SCN_00089,
            ),
            ok: {
              evntGrpTypeCd: EvntGrp.EVNT_GRP_PJ_010_013,
              changedMassage: messageForScreen(
                ApplicationMessageScreen.APP_SCN_00102,
              ),
            },
          });
        },
        prerequisiteActionIds: [],
        enableStatus: [
          {
            tcktTypeCd: TcktType.PROJECT_PLAN,
            tcktStsTypeCd:
              TcktStsTypePrjPlan.PROJECT_PLANNING_FINAL_CHECK_IN_PROGRESS,
          },
        ],
        role: [RoleType.PIC_OF_IF_RD],
      },
      {
        actionId: "a0512",
        name: "Cancel Request to Review Project Plan",
        description: ActionDesctription.a9090,
        required: false,
        type: ActionType.StatusBack,
        actionMethod: () => {
          updateStatusRequest({
            tcktId: project.planStatus?.tcktId,
            dialogType: DialogType.ConfirmWithComment,
            confirmMessage: messageForScreen(
              ApplicationMessageScreen.APP_SCN_00088,
            ),
            ok: {
              evntGrpTypeCd: EvntGrp.EVNT_GRP_PJ_010_010,
              changedMassage: messageForScreen(
                ApplicationMessageScreen.APP_SCN_00101,
              ),
            },
          });
        },
        prerequisiteActionIds: [],
        enableStatus: [
          {
            tcktTypeCd: TcktType.PROJECT_PLAN,
            tcktStsTypeCd:
              TcktStsTypePrjPlan.PROJECT_PLANNING_FINAL_CHECK_IN_PROGRESS,
          },
        ],
        role: [RoleType.PROJECT_OWNER],
        isNotDisplay:
          project.costEstimation.pjtBdgtAcqstnReqTypeCd ==
          InDefCommonFlagType.NO,
      },
      {
        actionId: "a0590",
        name: "Judgement to Complete Project Plan",
        description: ActionDesctription.a0590,
        required: true,
        type: ActionType.ChangeStatus,
        actionMethod: () => {
          updateStatusRequest({
            tcktId: project.planStatus?.tcktId,
            dialogType: DialogType.Judgement,
            confirmMessage: messageForScreen(
              ApplicationMessageScreen.APP_SCN_00086,
            ),
            ok: {
              evntGrpTypeCd: EvntGrp.EVNT_GRP_PJ_010_008,
              changedMassage: messageForScreen(
                ApplicationMessageScreen.APP_SCN_00096,
              ),
              postProcessParams: {
                executionTcktId: project.executionStatus?.tcktId,
              },
            },
            ng: {
              evntGrpTypeCd:
                project.costEstimation.pjtBdgtAcqstnReqTypeCd ==
                InDefCommonFlagType.YES
                  ? EvntGrp.EVNT_GRP_PJ_010_009
                  : EvntGrp.EVNT_GRP_PJ_010_015,
              changedMassage: messageForScreen(
                ApplicationMessageScreen.APP_SCN_00097,
              ),
            },
          });
        },
        prerequisiteActionIds: [],
        enableStatus: [
          {
            tcktTypeCd: TcktType.PROJECT_PLAN,
            tcktStsTypeCd:
              TcktStsTypePrjPlan.PROJECT_PLANNING_FINAL_CHECK_IN_PROGRESS,
          },
        ],
        role: [RoleType.DIP_MANAGER],
      },
      {
        actionId: "a0601",
        name: "Design Interface Requirement List",
        description: ActionDesctription.a0601,
        required: true,
        type: ActionType.SubTask,
        actionMethod: () => {
          navigate(`/interface-requirement-list?pjtId=${pjtId}`);
        },
        prerequisiteActionIds: [],
        enableStatus: [
          {
            tcktTypeCd: TcktType.PROJECT_PROGRESS,
            tcktStsTypeCd: TcktStsTypePrjPrgrs.IF_REQUIREMENT_DESIGN,
          },
        ],
        role: [RoleType.PIC_OF_IF_RD],
      },
      {
        actionId: "a0602",
        name: "Link Component Project",
        description: ActionDesctription.a0602,
        required: true,
        type: ActionType.SubTask,
        actionMethod: () => {
          showComponentProjectDialog();
        },
        prerequisiteActionIds: [],
        enableStatus: [
          {
            tcktTypeCd: TcktType.PROJECT_PROGRESS,
            tcktStsTypeCd: TcktStsTypePrjPrgrs.IF_REQUIREMENT_DESIGN,
          },
        ],
        role: [RoleType.PIC_OF_IF_RD],
      },
      {
        actionId: "a0690",
        name: "Determine IF Requirement",
        description: ActionDesctription.a0690,
        required: true,
        type: ActionType.ChangeStatus,
        actionMethod: () => {
          updateStatusRequest({
            tcktId: project.executionStatus?.tcktId,
            dialogType: DialogType.Confirm,
            confirmMessage: messageForScreen(
              ApplicationMessageScreen.APP_SCN_00087,
              "Determine",
            ),
            ok: {
              evntGrpTypeCd: EvntGrp.EVNT_GRP_PJ_020_001,
              changedMassage: messageForScreen(
                ApplicationMessageScreen.APP_SCN_00098,
              ),
              preProcessParams: {
                dfnSetVerNum: dfnSetVerNum,
                pjtId: pjtId,
              },
              postProcessParams: {
                dfnSetVerNum: dfnSetVerNum,
                pjtId: pjtId,
              },
            },
          });
        },
        prerequisiteActionIds: ["a0601", "a0602"],
        enableStatus: [
          {
            tcktTypeCd: TcktType.PROJECT_PROGRESS,
            tcktStsTypeCd: TcktStsTypePrjPrgrs.IF_REQUIREMENT_DESIGN,
          },
        ],
        role: [RoleType.PIC_OF_IF_RD],
      },
      {
        actionId: "a0701",
        name: "Assign Stakeholders",
        description: ActionDesctription.a0701,
        required: true,
        type: ActionType.SubTask,
        actionMethod: showProjectStakeholderList,
        prerequisiteActionIds: [],
        enableStatus: [
          {
            tcktTypeCd: TcktType.PROJECT_PROGRESS,
            tcktStsTypeCd: TcktStsTypePrjPrgrs.ASSIGNING_STAKEHOLDERS,
          },
        ],
        role: [RoleType.PROJECT_OWNER],
      },
      {
        actionId: "a0711",
        name: "Reconsider IF Requirement",
        description: ActionDesctription.a9290,
        required: false,
        type: ActionType.StatusBack,
        actionMethod: () => {
          updateStatusRequest({
            tcktId: project.executionStatus?.tcktId,
            dialogType: DialogType.Confirm,
            confirmMessage: messageForScreen(
              ApplicationMessageScreen.APP_SCN_00087,
              "Reconsider",
            ),
            ok: {
              evntGrpTypeCd: EvntGrp.EVNT_GRP_PJ_020_005,
              changedMassage: messageForScreen(
                ApplicationMessageScreen.APP_SCN_00105,
              ),
            },
          });
        },
        prerequisiteActionIds: [],
        enableStatus: [
          {
            tcktTypeCd: TcktType.PROJECT_PROGRESS,
            tcktStsTypeCd: TcktStsTypePrjPrgrs.ASSIGNING_STAKEHOLDERS,
          },
        ],
        role: [RoleType.PIC_OF_IF_RD],
      },
      {
        actionId: "a0790",
        name: "Start Project",
        description: ActionDesctription.a0790,
        required: true,
        type: ActionType.ChangeStatus,
        actionMethod: () => {
          updateStatusRequest({
            tcktId: project.executionStatus?.tcktId,
            dialogType: DialogType.Confirm,
            confirmMessage: messageForScreen(
              ApplicationMessageScreen.APP_SCN_00081,
              "Start",
            ),
            ok: {
              evntGrpTypeCd: EvntGrp.EVNT_GRP_PJ_020_002,
              changedMassage: messageForScreen(
                ApplicationMessageScreen.APP_SCN_00099,
              ),
              preProcessParams: {
                dfnSetVerNum: dfnSetVerNum,
                pjtId: pjtId,
              },
              postProcessParams: {
                dfnSetVerNum: dfnSetVerNum,
                pjtId: pjtId,
              },
            },
          });
        },
        prerequisiteActionIds: ["a0701"],
        enableStatus: [
          {
            tcktTypeCd: TcktType.PROJECT_PROGRESS,
            tcktStsTypeCd: TcktStsTypePrjPrgrs.ASSIGNING_STAKEHOLDERS,
          },
        ],
        role: [RoleType.PROJECT_OWNER],
      },
      {
        actionId: "a0801",
        name: "Check development progress",
        description: ActionDesctription.a0801,
        required: true,
        type: ActionType.SubTask,
        actionMethod: () => {
          navigate(`/project-detail?pjtId=${pjtId}`);
        },
        prerequisiteActionIds: [],
        enableStatus: [
          {
            tcktTypeCd: TcktType.PROJECT_PROGRESS,
            tcktStsTypeCd: TcktStsTypePrjPrgrs.PROJECT_IN_PROGRESS,
          },
        ],
        role: [],
      },
      {
        actionId: "a0811",
        name: "Reconsider IF Requirement",
        description: ActionDesctription.a9290,
        required: false,
        type: ActionType.StatusBack,
        actionMethod: () => {
          updateStatusRequest({
            tcktId: project.executionStatus?.tcktId,
            dialogType: DialogType.Confirm,
            confirmMessage: messageForScreen(
              ApplicationMessageScreen.APP_SCN_00087,
              "Reconsider",
            ),
            ok: {
              evntGrpTypeCd: EvntGrp.EVNT_GRP_PJ_020_006,
              changedMassage: messageForScreen(
                ApplicationMessageScreen.APP_SCN_00105,
              ),
            },
          });
        },
        prerequisiteActionIds: [],
        enableStatus: [
          {
            tcktTypeCd: TcktType.PROJECT_PROGRESS,
            tcktStsTypeCd: TcktStsTypePrjPrgrs.PROJECT_IN_PROGRESS,
          },
        ],
        role: [RoleType.PIC_OF_IF_RD],
      },
      {
        actionId: "a0890",
        name: "Complete Project",
        description: ActionDesctription.a0890,
        required: true,
        type: ActionType.ChangeStatus,
        actionMethod: () => {
          updateStatusRequest({
            tcktId: project.executionStatus?.tcktId,
            dialogType: DialogType.Confirm,
            confirmMessage: messageForScreen(
              ApplicationMessageScreen.APP_SCN_00081,
              "Complete",
            ),
            ok: {
              evntGrpTypeCd: EvntGrp.EVNT_GRP_PJ_020_003,
              changedMassage: messageForScreen(
                ApplicationMessageScreen.APP_SCN_00100,
              ),
              preProcessParams: {
                pjtId: pjtId,
              },
            },
          });
        },
        prerequisiteActionIds: ["a0801"],
        enableStatus: [
          {
            tcktTypeCd: TcktType.PROJECT_PROGRESS,
            tcktStsTypeCd: TcktStsTypePrjPrgrs.PROJECT_IN_PROGRESS,
          },
        ],
        role: [RoleType.PROJECT_OWNER],
      },
      {
        actionId: "a0901",
        name: "Design Interface Requirement List",
        description: ActionDesctription.a0601,
        required: false,
        type: ActionType.SubTask,
        actionMethod: () => {
          navigate(`/interface-requirement-list?pjtId=${pjtId}`);
        },
        prerequisiteActionIds: [],
        enableStatus: [
          {
            tcktTypeCd: TcktType.PROJECT_PROGRESS,
            tcktStsTypeCd: TcktStsTypePrjPrgrs.CHANGING_IF_REQUIREMENTS,
          },
        ],
        role: [RoleType.PIC_OF_IF_RD],
      },
      {
        actionId: "a0903",
        name: "Assign Stakeholders",
        description: ActionDesctription.a0701,
        required: true,
        type: ActionType.SubTask,
        actionMethod: showProjectStakeholderList,
        prerequisiteActionIds: [],
        enableStatus: [
          {
            tcktTypeCd: TcktType.PROJECT_PROGRESS,
            tcktStsTypeCd: TcktStsTypePrjPrgrs.CHANGING_IF_REQUIREMENTS,
          },
        ],
        role: [RoleType.PIC_OF_IF_RD],
      },
      {
        actionId: "a0902",
        name: "Link Component Project",
        description: ActionDesctription.a0602,
        required: true,
        type: ActionType.SubTask,
        actionMethod: () => {
          showComponentProjectDialog();
        },
        prerequisiteActionIds: [],
        enableStatus: [
          {
            tcktTypeCd: TcktType.PROJECT_PROGRESS,
            tcktStsTypeCd: TcktStsTypePrjPrgrs.CHANGING_IF_REQUIREMENTS,
          },
        ],
        role: [RoleType.PIC_OF_IF_RD],
      },
      {
        actionId: "a0990",
        name: "Redetermine IF Requirement",
        description: ActionDesctription.a9390,
        required: true,
        type: ActionType.StatusBack,
        actionMethod: () => {
          updateStatusRequest({
            tcktId: project.executionStatus?.tcktId,
            dialogType: DialogType.Confirm,
            confirmMessage: messageForScreen(
              ApplicationMessageScreen.APP_SCN_00087,
              "Redetermine",
            ),
            ok: {
              evntGrpTypeCd: EvntGrp.EVNT_GRP_PJ_020_007,
              changedMassage: messageForScreen(
                ApplicationMessageScreen.APP_SCN_00103,
              ),
              preProcessParams: {
                dfnSetVerNum: dfnSetVerNum,
                pjtId: pjtId,
              },
              postProcessParams: {
                dfnSetVerNum: dfnSetVerNum,
                pjtId: pjtId,
              },
            },
          });
        },
        prerequisiteActionIds: ["a0901", "a0902", "a0903"],
        enableStatus: [
          {
            tcktTypeCd: TcktType.PROJECT_PROGRESS,
            tcktStsTypeCd: TcktStsTypePrjPrgrs.CHANGING_IF_REQUIREMENTS,
          },
        ],
        role: [RoleType.PIC_OF_IF_RD],
      },
      {
        actionId: "a9190",
        name: "Re-Estimate",
        description: ActionDesctription.a9190,
        required: false,
        type: ActionType.StatusBack,
        actionMethod: () => {
          updateStatusRequest({
            tcktId: project.planStatus?.tcktId,
            dialogType: DialogType.Confirm,
            confirmMessage: messageForScreen(
              ApplicationMessageScreen.APP_SCN_00089,
            ),
            ok: {
              evntGrpTypeCd: EvntGrp.EVNT_GRP_PJ_010_011,
              changedMassage: messageForScreen(
                ApplicationMessageScreen.APP_SCN_00102,
              ),
              preProcessParams: {
                executionTcktId: project.executionStatus?.tcktId,
              },
            },
          });
        },
        prerequisiteActionIds: [],
        enableStatus: [
          {
            tcktTypeCd: TcktType.PROJECT_PROGRESS,
            tcktStsTypeCd: TcktStsTypePrjPrgrs.IF_REQUIREMENT_DESIGN,
          },
          {
            tcktTypeCd: TcktType.PROJECT_PROGRESS,
            tcktStsTypeCd: TcktStsTypePrjPrgrs.ASSIGNING_STAKEHOLDERS,
          },
          {
            tcktTypeCd: TcktType.PROJECT_PROGRESS,
            tcktStsTypeCd: TcktStsTypePrjPrgrs.CHANGING_IF_REQUIREMENTS,
          },
          {
            tcktTypeCd: TcktType.PROJECT_PROGRESS,
            tcktStsTypeCd: TcktStsTypePrjPrgrs.PROJECT_IN_PROGRESS,
          },
        ],
        role: [RoleType.PIC_OF_IF_RD],
      },
    ];

    let currentTcktTypeCd = TcktType.PROJECT_PLAN;
    let currentTcktStsTypeCd = "00";

    if (
      project.planStatus !== null &&
      project.planStatus !== undefined &&
      project.executionStatus !== null &&
      project.executionStatus !== undefined
    ) {
      currentTcktTypeCd =
        project.planStatus?.stsId !== TcktStsTypePrjPlan.PROJECT_PLANNED
          ? TcktType.PROJECT_PLAN
          : TcktType.PROJECT_PROGRESS;
      currentTcktStsTypeCd =
        currentTcktTypeCd === TcktType.PROJECT_PLAN
          ? project.planStatus.stsId
          : project.executionStatus.stsId;
    }

    const filteredActionItems = initialActionItems
      .filter((item) =>
        item.enableStatus.find(
          (sts) =>
            sts.tcktTypeCd === currentTcktTypeCd &&
            sts.tcktStsTypeCd === currentTcktStsTypeCd,
        ),
      )
      .filter(
        (item) =>
          item.role.length === 0 ||
          item.role.some((e) => loginUserRoleList.includes(e)),
      )
      .filter((item) => !item.isNotDisplay)
      .map((item) => {
        return {
          ...item,
          isMetPrereq: item.prerequisiteActionIds.some(
            (e) => !actionCompleted(e),
          )
            ? false
            : true,
          isCompleted: actionCompleted(item.actionId),
        };
      });

    if (filteredActionItems.length > 0) {
      return filteredActionItems;
    }

    return [
      {
        actionId: "",
        name: "NoTask",
        description: "Tasks are being proceeded by other stakeholders.",
        required: false,
        type: "",
        actionMethod: () => {},
        prerequisiteActionIds: [],
        role: [],
        enableStatus: [],
      },
    ];
  };

  const actionCompleted = (actionId: string) => {
    switch (actionId) {
      case "a0001":
        return (
          project.projectInformation.pjtName !== undefined &&
          project.projectInformation.pjtName !== null
        );
      case "a0002":
        return project.stakeholderList.some(
          (e) =>
            e.roleCd === RoleType.PROJECT_OWNER &&
            e.primFlg === InDefCommonFlagType.YES,
        );
      case "a0101":
        return project.stakeholderList.some(
          (e) =>
            e.roleCd === RoleType.PIC_OF_IF_RD &&
            e.primFlg === InDefCommonFlagType.YES,
        );
      case "a0201":
        return (
          project.tstUseTypeCd !== undefined && project.tstUseTypeCd !== null
        );
      case "a0202":
        return (
          project.ifReqSTmpUpdLastDTime !== undefined &&
          project.ifReqSTmpUpdLastDTime !== null
        );
      case "a0301":
        return (
          project.costEstimation.pjtBdgtAcqstnReqTypeCd !== undefined &&
          project.costEstimation.pjtBdgtAcqstnReqTypeCd !== null
        );
      case "a0601":
      case "a0602":
        return project.executionStatus?.availebleNxtStsIds.includes(
          TcktStsTypePrjPrgrs.ASSIGNING_STAKEHOLDERS,
        );
      case "a0701":
      case "a0901":
      case "a0902":
      case "a0903":
        return project.executionStatus?.availebleNxtStsIds.includes(
          TcktStsTypePrjPrgrs.PROJECT_IN_PROGRESS,
        );
      case "a0801":
        return project.executionStatus?.availebleNxtStsIds.includes(
          TcktStsTypePrjPrgrs.PROJECT_COMPLETE,
        );
    }
    return false;
  };

  const actionItem = (action: ActionItem) => {
    if (action.name === "NoTask") {
      return (
        <>
          <FrVrfTextDisplayField
            style={{
              "--vrf-text-display-field-font-weight": "400",
              "--vrf-text-display-field-font-size": "16px",
              width: "60%",
              display: "inline-block",
              verticalAlign: "middle",
            }}
            text={action.description}
          />
        </>
      );
    }

    let mandatory = !action.required
      ? "Optional"
      : action.isCompleted
        ? "Done"
        : "Required";
    let mandFontColor = !action.required
      ? "green"
      : action.isCompleted
        ? "gray"
        : "red";
    let mandFontWeight =
      action.required && action.isMetPrereq && !action.isCompleted
        ? "600"
        : "400";
    let nameFontColor = action.type === ActionType.StatusBack ? "red" : "black";
    let nameFontWeight =
      action.type === ActionType.ChangeStatus ? "600" : "400";
    let descFontWeight =
      action.type === ActionType.ChangeStatus ? "600" : "400";

    return (
      <>
        <div
          style={{
            borderBottomStyle: "solid",
            borderBottomWidth: "1px",
            borderBottomColor: VRF_HORIZONTAL_SEPARATOR_COLOR,
          }}
        >
          <FrVrfTextDisplayField
            style={{
              "--vrf-font-color": `${mandFontColor}`,
              "--vrf-text-display-field-font-weight": `${mandFontWeight}`,
              minWidth: "50px",
              width: "7%",
              display: "inline-block",
              verticalAlign: "bottom",
            }}
            text={mandatory}
          />
          {action.actionId === "a0202" ? (
            <FrVrfFileUploadField
              style={{
                "--vrf-file-upload-field-button-margin": "7px",
                minWidth: "80px",
                width: "8%",
                marginRight: "22px",
              }}
              uploadBtnText="Action"
              accept=".xlsx"
              onFileSelected={uploadIfReqSheetRequest}
            />
          ) : (
            <FrVrfButton
              content="Action"
              style={{
                minWidth: "80px",
                width: "8%",
                paddingTop: "7px",
                marginLeft: "14px",
                marginRight: "8px",
              }}
              disabled={!action.isMetPrereq}
              theme="secondary"
              size="small"
              onClicked={action.actionMethod}
            />
          )}
          <FrVrfTextDisplayField
            style={{
              "--vrf-text-display-field-font-size": "16px",
              "--vrf-font-color": `${nameFontColor}`,
              "--vrf-text-display-field-font-weight": `${nameFontWeight}`,
              width: "30%",
              display: "inline-block",
              verticalAlign: "bottom",
            }}
            text={action.name}
          />
          <FrVrfTextDisplayField
            style={{
              "--vrf-text-display-field-font-weight": `${descFontWeight}`,
              display: "inline-block",
              verticalAlign: "bottom",
            }}
            text={action.description}
          />
        </div>
      </>
    );
  };

  return (
    <>
      <FrVrfPanel panelTitle="Action Items" columns={1}>
        {getActionItems().map((action, index) => {
          return <div key={index}>{actionItem(action)}</div>;
        })}
      </FrVrfPanel>
    </>
  );
});

type ActionItem = {
  actionId: string;
  name: string;
  description: string;
  required: boolean;
  type: string;
  actionMethod: () => void;
  prerequisiteActionIds: string[];
  role: string[];
  enableStatus: {
    tcktTypeCd: string;
    tcktStsTypeCd: string;
  }[];
  isNotDisplay?: boolean;
  isMetPrereq?: boolean;
  isCompleted?: boolean;
};
